import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    Button,
    Chip,
    Grid,
    Table,
    TableBody,
    TableHead,
    Typography,
} from "@mui/material";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { getStudentFeeRecords } from "../../redux/feeRelated/feeHandle";
import StudentFeePaymentDialog from "../../components/student/StudentFeePaymentDialog";
import { StyledTableCell, StyledTableRow } from "../../components/styles";
import {
    StudentPageContainer,
    StudentPageHeader,
    StudentPageLoading,
    StudentStatCard,
    StudentEmptyState,
    ResultsSectionPaper,
    ResponsiveTableWrap,
} from "./studentPortalUi";

const formatMWK = (n) => `MWK ${Number(n || 0).toLocaleString()}`;

function statusChipColor(status) {
    const s = String(status || "");
    if (s === "Unpaid") return "error";
    if (s === "Partial") return "warning";
    if (s === "Paid") return "success";
    if (s === "Waived") return "info";
    if (s === "Overpaid") return "secondary";
    return "default";
}

function submissionChipColor(status) {
    if (status === "Pending") return "warning";
    if (status === "Approved") return "success";
    if (status === "Rejected") return "error";
    return "default";
}

function termLabel(record) {
    const fs = record?.feeStructure;
    if (!fs) return "Fee";
    const parts = [fs.term, fs.academicYear].filter(Boolean);
    return parts.length ? parts.join(" · ") : "School fees";
}

const StudentFees = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((s) => s.user);
    const { studentFeeRecords, loading, error } = useSelector((s) => s.fee);
    const [payOpen, setPayOpen] = useState(false);

    const studentId = currentUser?._id || currentUser?.id;

    const reload = () => {
        if (studentId) dispatch(getStudentFeeRecords(studentId));
    };

    useEffect(() => {
        reload();
    }, [dispatch, studentId]); // eslint-disable-line react-hooks/exhaustive-deps

    const records = useMemo(() => {
        const raw = studentFeeRecords?.records;
        return Array.isArray(raw) ? raw : [];
    }, [studentFeeRecords]);

    const submissions = useMemo(() => {
        const raw = studentFeeRecords?.submissions;
        return Array.isArray(raw) ? raw : [];
    }, [studentFeeRecords]);

    const paymentSettings = studentFeeRecords?.paymentSettings;

    const summary = studentFeeRecords?.summary || {
        totalDue: 0,
        totalPaid: 0,
        totalBalance: 0,
    };

    const outstanding = Number(summary.totalBalance) || 0;
    const pendingCount = submissions.filter((s) => s.status === "Pending").length;

    if (loading && !studentFeeRecords) {
        return <StudentPageLoading />;
    }

    return (
        <StudentPageContainer maxWidth="lg">
            <StudentPageHeader
                eyebrow="Finance"
                title="My fees"
                subtitle="Live balance from your school. Pay using bank details below and upload proof for verification."
                action={
                    outstanding > 0 ? (
                        <Button variant="contained" sx={{ fontWeight: 700 }} onClick={() => setPayOpen(true)}>
                            Make payment
                        </Button>
                    ) : null
                }
            />

            {error ? (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    Could not load fee records. Please try again later.
                </Alert>
            ) : null}

            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <StudentStatCard
                        title="Total billed"
                        value={Math.round(summary.totalDue)}
                        hint="All fee structures"
                        icon={ReceiptLongOutlinedIcon}
                        color="primary"
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StudentStatCard
                        title="Paid"
                        value={Math.round(summary.totalPaid)}
                        hint="Verified by school"
                        icon={PaymentsOutlinedIcon}
                        color="success"
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StudentStatCard
                        title="Balance"
                        value={Math.round(outstanding)}
                        hint={outstanding > 0 ? "Amount still owed" : "Up to date"}
                        icon={outstanding > 0 ? WarningAmberOutlinedIcon : AccountBalanceOutlinedIcon}
                        color={outstanding > 0 ? "warning" : "success"}
                    />
                </Grid>
            </Grid>

            {paymentSettings?.hasPaymentDetails ? (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        School payment details
                    </Typography>
                    {paymentSettings.feeBankName ? (
                        <Typography variant="body2">
                            Bank: <strong>{paymentSettings.feeBankName}</strong>
                            {paymentSettings.feeBankBranch ? ` · ${paymentSettings.feeBankBranch}` : ""}
                        </Typography>
                    ) : null}
                    {paymentSettings.feeAccountNumber ? (
                        <Typography variant="body2">
                            Account: <strong>{paymentSettings.feeAccountNumber}</strong>
                            {paymentSettings.feeAccountHolderName
                                ? ` (${paymentSettings.feeAccountHolderName})`
                                : ""}
                        </Typography>
                    ) : null}
                    {paymentSettings.feeMobileMoneyNumber ? (
                        <Typography variant="body2">
                            Mobile money: <strong>{paymentSettings.feeMobileMoneyNumber}</strong>
                        </Typography>
                    ) : null}
                    {paymentSettings.feePaymentInstructions ? (
                        <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                            {paymentSettings.feePaymentInstructions}
                        </Typography>
                    ) : null}
                </Alert>
            ) : (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    Your director has not published bank payment details yet. Ask the school office before paying online.
                </Alert>
            )}

            {pendingCount > 0 ? (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    You have <strong>{pendingCount}</strong> payment submission{pendingCount === 1 ? "" : "s"} awaiting
                    verification by the director or bursar.
                </Alert>
            ) : null}

            {outstanding > 0 ? (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    Outstanding balance: <strong>{formatMWK(outstanding)}</strong>. Use <strong>Make payment</strong> to
                    upload your bank transfer or mobile money proof.
                </Alert>
            ) : null}

            {records.length === 0 ? (
                <StudentEmptyState
                    icon={AccountBalanceOutlinedIcon}
                    title="No fee records yet"
                    description="When your school assigns fees to your account, they will appear here with term, status, and balance."
                />
            ) : (
                <ResultsSectionPaper title="Fee statements">
                    <ResponsiveTableWrap>
                        <Table size="small">
                            <TableHead>
                                <StyledTableRow>
                                    <StyledTableCell>Term / year</StyledTableCell>
                                    <StyledTableCell>Class</StyledTableCell>
                                    <StyledTableCell align="right">Billed</StyledTableCell>
                                    <StyledTableCell align="right">Paid</StyledTableCell>
                                    <StyledTableCell align="right">Balance</StyledTableCell>
                                    <StyledTableCell>Status</StyledTableCell>
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {records.map((row) => {
                                    const id = row.id || row._id;
                                    const balance = Number(row.balance) || 0;
                                    return (
                                        <StyledTableRow key={id}>
                                            <StyledTableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {termLabel(row)}
                                                </Typography>
                                            </StyledTableCell>
                                            <StyledTableCell>{row.sclass?.sclassName || "—"}</StyledTableCell>
                                            <StyledTableCell align="right">{formatMWK(row.totalDue)}</StyledTableCell>
                                            <StyledTableCell align="right">{formatMWK(row.totalPaid)}</StyledTableCell>
                                            <StyledTableCell
                                                align="right"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: balance > 0 ? "error.main" : "success.main",
                                                }}
                                            >
                                                {formatMWK(balance)}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <Chip
                                                    size="small"
                                                    label={row.status || "—"}
                                                    color={statusChipColor(row.status)}
                                                />
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </ResponsiveTableWrap>
                </ResultsSectionPaper>
            )}

            {submissions.length > 0 ? (
                <ResultsSectionPaper title="Your payment submissions">
                    <ResponsiveTableWrap>
                        <Table size="small">
                            <TableHead>
                                <StyledTableRow>
                                    <StyledTableCell>Date</StyledTableCell>
                                    <StyledTableCell>Amount</StyledTableCell>
                                    <StyledTableCell>Method</StyledTableCell>
                                    <StyledTableCell>Reference</StyledTableCell>
                                    <StyledTableCell>Status</StyledTableCell>
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {submissions.map((row) => {
                                    const id = row.id || row._id;
                                    return (
                                        <StyledTableRow key={id}>
                                            <StyledTableCell>
                                                {row.createdAt
                                                    ? new Date(row.createdAt).toLocaleDateString()
                                                    : "—"}
                                            </StyledTableCell>
                                            <StyledTableCell>{formatMWK(row.amount)}</StyledTableCell>
                                            <StyledTableCell>{row.paymentMethod || "—"}</StyledTableCell>
                                            <StyledTableCell>{row.referenceNumber || "—"}</StyledTableCell>
                                            <StyledTableCell>
                                                <Chip
                                                    size="small"
                                                    label={row.status || "—"}
                                                    color={submissionChipColor(row.status)}
                                                />
                                                {row.status === "Rejected" && row.rejectionReason ? (
                                                    <Typography variant="caption" color="error" display="block">
                                                        {row.rejectionReason}
                                                    </Typography>
                                                ) : null}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </ResponsiveTableWrap>
                </ResultsSectionPaper>
            ) : null}

            <StudentFeePaymentDialog
                open={payOpen}
                onClose={() => setPayOpen(false)}
                studentId={studentId}
                records={records}
                paymentSettings={paymentSettings}
                onSubmitted={reload}
            />
        </StudentPageContainer>
    );
};

export default StudentFees;
